// Stage A Pilot — the Head-of-Testing reasoning pilot's operator panel
// (REASONING-STAGE-A-OPERATOR-PATH, narrowed by the Founder scope correction).
//
// EXACTLY THE FOUNDER'S FIVE ACTIONS, PLUS READ-ONLY EVIDENCE
// ===========================================================
//   (a) review + approve formal activation of the PRODUCTION Head — via the
//       EXISTING founder route POST /api/organization/agents/{id}/activate;
//   (b) review the frozen snapshot: id, hash, digest
//       (POST/GET /api/organization/reasoning/stage-a/snapshot);
//   (c) confirm the private prediction is written — a bare attestation
//       checkbox that ARMS Execute. The prediction itself is an external
//       paper/Notes artifact; Cipher stores no prediction content, ever;
//   (d) trigger the ONE-TIME execution (the server refuses re-execution per
//       snapshot with a 409 carrying the run id);
//   (e) review the recorded decision — REVIEW-ONLY. This panel exposes no
//       control that approves, dispatches or executes the proposal during
//       Stage A: the task simply shows awaiting its founder gate, with ids.
// Read-only cards: rehearsal pass/fail evidence and the materialize
// auto-run status / held report, both written by the post-deploy automation.
// Nothing else. Buttons never chain — every step is an explicit click, and
// the server enforces the ordering regardless (R5: the client gates nothing).
//
// Errors are surfaced VERBATIM: the backend's detail strings are written to
// be diagnostic (e.g. binding's "cannot be bound" for a dormant Head), and
// paraphrasing them here would hide the diagnosis.
import axios from "axios";
import React, { useCallback, useState } from "react";

import PageHead, { SectionTitle } from "../../components/PageHead";
import MaterializeApproval from "./MaterializeApproval";
import { API } from "../../data/client";
import useResource from "../../data/useResource";
import "./stage-a.css";

const BASE = "/organization/reasoning/stage-a";

/** POST helper, same transport posture as the shell's reader: credentials
 * always sent, refusals distinguished from failures, never throws. */
async function post(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, {
      withCredentials: true,
    });
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    const httpStatus = err?.response?.status ?? null;
    const detail =
      err?.response?.data?.detail || err?.message || "Request failed";
    return { status: "error", detail: String(detail), httpStatus };
  }
}

function Card({ title, note, children }) {
  return (
    <section className="stage-a-card">
      <SectionTitle>{title}</SectionTitle>
      {note && <p className="stage-a-note">{note}</p>}
      {children}
    </section>
  );
}

/** A backend detail string, shown verbatim. */
function ErrorLine({ error }) {
  if (!error) return null;
  return (
    <p className="stage-a-error" role="alert">
      {error.httpStatus ? `HTTP ${error.httpStatus} — ` : ""}
      {error.detail}
    </p>
  );
}

function Mono({ children }) {
  return <code className="stage-a-mono">{children}</code>;
}

function CopyButton({ value, label }) {
  const [state, setState] = useState("idle");
  return (
    <button
      type="button"
      className="stage-a-btn stage-a-btn-small"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setState("copied");
        } catch (e) {
          setState("failed");
        }
      }}
    >
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label}
    </button>
  );
}

// ── read-only evidence cards ────────────────────────────────────────────────

function RehearsalCard({ rehearsals }) {
  if (!rehearsals || rehearsals.length === 0) {
    return (
      <p className="stage-a-dim">
        No rehearsal has been recorded yet. Rehearsals run automatically after
        a deploy (once per commit) — nothing to click here.
      </p>
    );
  }
  return (
    <table className="stage-a-table">
      <thead>
        <tr>
          <th>Status</th>
          <th>Commit</th>
          <th>Rehearsal DB</th>
          <th>Steps</th>
          <th>Run</th>
        </tr>
      </thead>
      <tbody>
        {rehearsals.map((r) => (
          <tr key={r.rehearsal_id}>
            <td>
              <span className={`stage-a-status stage-a-${r.status}`}>
                {r.status}
              </span>
            </td>
            <td>
              <Mono>{(r.commit_sha || "").slice(0, 12) || "—"}</Mono>
            </td>
            <td>
              <Mono>{r.db_name}</Mono>
            </td>
            <td className="stage-a-steps">
              {Object.entries(r.steps || {})
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ")}
            </td>
            <td>
              <Mono>{r.run_id || "—"}</Mono>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MaterializeCard({ runs }) {
  if (!runs || runs.length === 0) {
    return (
      <p className="stage-a-dim">
        No auto-materialize run has been recorded yet. Materialize runs
        automatically after a deploy, through the invariant gate.
      </p>
    );
  }
  const latest = runs[0];
  return (
    <div>
      {latest.outcome === "held" && (
        <div className="stage-a-banner" role="alert">
          <strong>Materialize HELD — founder review required.</strong> The
          dry-run report did not match the strict expectation, so nothing was
          applied. Review the deviations below; applying is done through the
          existing founder materialize route, not from this panel.
          <ul>
            {(latest.deviations || []).map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}
      <table className="stage-a-table">
        <thead>
          <tr>
            <th>Outcome</th>
            <th>Commit</th>
            <th>Dry-run counts</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((m, i) => (
            <tr key={`${m.commit_sha}-${i}`}>
              <td>
                <span
                  className={`stage-a-status stage-a-${
                    m.outcome === "held" ? "failed" : "passed"
                  }`}
                >
                  {m.outcome}
                </span>
              </td>
              <td>
                <Mono>{(m.commit_sha || "").slice(0, 12)}</Mono>
              </td>
              <td className="stage-a-steps">
                {m.dry_run_counts
                  ? `created ${m.dry_run_counts.created ?? "?"} · updated ${
                      m.dry_run_counts.updated ?? "?"
                    } · unchanged ${m.dry_run_counts.unchanged ?? "?"}`
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Every dormant specialist, each with the SAME ActivationCard and therefore
 *  the same confirm text, the same grant path and the same readiness warning.
 *
 *  A list rather than a script: activation mints capability grants, and the
 *  only legitimate way to do that is a request that has actually passed
 *  authentication (`authority.from_operator` says so in as many words). A
 *  session cannot mint founder authority for itself, so the Founder clicks —
 *  but he clicks once per agent in one place, not once per page. */
//: The states that are genuinely a failed read. Everything else — `loading`,
//: `idle`, `ready` — is not, and must never render as one.
//:
//: `useResource` sets `status: "loading"` while KEEPING the previous data, so
//: after each activation `onChanged -> reload()` leaves rows on screen with a
//: loading status. Testing `status !== "ready"` therefore reported a failure on
//: every successful refresh, carrying the last success's httpStatus with it —
//: "HTTP 200 — Could not read the roster", every time, while the activations
//: underneath were all working.
const READ_FAILED = ["refused", "offline", "failed"];

function readFailure(res, { refused, offline, failed }) {
  if (!READ_FAILED.includes(res.status)) return null;
  return {
    httpStatus: res.httpStatus,
    detail:
      res.status === "refused" ? refused
        : res.status === "offline" ? offline
        : failed,
  };
}

function DormantSpecialists() {
  const list = useResource("/organization/agents?layer=specialist&lifecycle=dormant");
  const rows = (list.data && list.data.agents) || [];
  const failure = readFailure(list, {
    refused: "Refused — this account cannot list agents.",
    offline: "The backend is unreachable, so the roster is unknown.",
    failed: "Could not read the roster.",
  });
  if (failure) return <ErrorLine error={failure} />;
  if (!rows.length) {
    // "still reading" and "genuinely none left" are different answers.
    return list.status === "ready" ? (
      <p className="stage-a-dim">No dormant specialists. Nothing to activate.</p>
    ) : (
      <p className="stage-a-dim">Reading the roster…</p>
    );
  }
  return (
    <>
      <p className="stage-a-dim">{rows.length} dormant.</p>
      {rows.map((a) => (
        <div key={a.agent_id} className="stage-a-subrow">
          <ActivationCard
            head={a}
            headAgentId={a.agent_id}
            onChanged={list.reload}
            roleLabel={a.title}
            readiness={a.readiness || null}
          />
        </div>
      ))}
    </>
  );
}

const BRIEF_SPECIALIST_ID = "spc_11f7fc79f124";

/** Creative Brief & Concept Specialist — the same ActivationCard, fed by the
 *  existing `GET /organization/agents/{id}` route. Deliberately a thin wrapper
 *  rather than a copy, so the confirm text and the grant path cannot drift. */
function SpecialistActivation() {
  const agent = useResource(`/organization/agents/${BRIEF_SPECIALIST_ID}`);
  const rec = agent.data || null;
  // A refusal, an outage and a failed read are reported by name and none of
  // them renders a usable Activate button. A RELOAD is none of those — see
  // `READ_FAILED`.
  const failure = readFailure(agent, {
    refused: "Refused — this account cannot read that agent record.",
    offline: "The backend is unreachable, so the specialist's state is unknown.",
    failed: "Could not read the specialist's record.",
  });
  if (failure) return <ErrorLine error={failure} />;
  if (!rec) {
    return agent.status === "ready" ? (
      <ErrorLine error={{ detail: "The specialist's record came back empty." }} />
    ) : (
      <p className="stage-a-dim">Reading the specialist's record…</p>
    );
  }
  return (
    <ActivationCard
      head={rec}
      headAgentId={BRIEF_SPECIALIST_ID}
      onChanged={agent.reload}
      roleLabel="Creative Brief & Concept Specialist"
      readiness={rec?.readiness || null}
    />
  );
}

// ── (a) production Head activation ──────────────────────────────────────────

function ActivationCard({
  head,
  headAgentId,
  onChanged,
  roleLabel = "Head of Testing",
  // Activation does NOT gate on readiness: `lifecycle.py:230` records
  // `activated_despite_readiness` when readiness is anything but `passed`, and
  // activates anyway. So the confirm must SAY when that is what is about to
  // happen — the Founder should not learn it afterwards from a field.
  readiness = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);

  const lifecycle = head?.lifecycle || "unknown";
  const override = readiness && readiness !== "passed";
  const activate = async () => {
    if (
      !window.confirm(
        `Formally activate the PRODUCTION ${roleLabel} (dormant → live, ` +
          "with its card's activation Tool grants)?" +
          (override
            ? `\n\nReadiness is "${readiness}", not "passed". This will be ` +
              "recorded as activated_despite_readiness."
            : "")
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const res = await post(`/organization/agents/${headAgentId}/activate`, {
      reason: "Stage A pilot — formal founder activation via the Stage A panel",
    });
    setBusy(false);
    if (res.status === "ok") {
      setEvent(res.data);
      onChanged();
    } else {
      setError(res);
    }
  };

  return (
    <div>
      <p>
        {roleLabel} lifecycle:{" "}
        <span
          className={`stage-a-status stage-a-${
            lifecycle === "live" ? "passed" : "pending"
          }`}
        >
          {lifecycle}
        </span>{" "}
        <Mono>{headAgentId}</Mono>
      </p>
      {lifecycle === "dormant" && (
        <button
          type="button"
          className="stage-a-btn"
          disabled={busy}
          onClick={activate}
        >
          {busy ? "Activating…" : `Activate ${roleLabel}`}
        </button>
      )}
      {lifecycle === "live" && head?.activated_at && (
        <p className="stage-a-dim">Activated {String(head.activated_at)}.</p>
      )}
      <ErrorLine error={error} />
      {event && (
        <pre className="stage-a-pre">{JSON.stringify(event, null, 2)}</pre>
      )}
    </div>
  );
}

// ── (b) the frozen snapshot ─────────────────────────────────────────────────

function DigestTable({ digest }) {
  if (!digest) return null;
  return (
    <table className="stage-a-table">
      <thead>
        <tr>
          <th>Evidence</th>
          <th>Records</th>
          <th>Sample ids</th>
        </tr>
      </thead>
      <tbody>
        {digest.map((d) => (
          <tr key={d.key}>
            <td>{d.key}</td>
            <td>{d.count}</td>
            <td className="stage-a-steps">
              {(d.sample || [])
                .map((s) => `${s.id}${s.state ? ` (${s.state})` : ""}`)
                .join(", ") || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SnapshotCard({ snapshot, setSnapshot, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const exportSnapshot = async () => {
    setBusy(true);
    setError(null);
    const res = await post(`${BASE}/snapshot`, {});
    setBusy(false);
    if (res.status === "ok") {
      setSnapshot(res.data);
      onChanged();
    } else {
      setError(res);
    }
  };

  return (
    <div>
      <button
        type="button"
        className="stage-a-btn"
        disabled={busy}
        onClick={exportSnapshot}
      >
        {busy ? "Exporting…" : "Export snapshot"}
      </button>
      <ErrorLine error={error} />
      {snapshot && (
        <div className="stage-a-snapshot">
          <p>
            <strong>snapshot_id</strong> <Mono>{snapshot.snapshot_id}</Mono>{" "}
            <CopyButton value={snapshot.snapshot_id} label="Copy id" />
          </p>
          <p>
            <strong>snapshot_hash</strong> <Mono>{snapshot.snapshot_hash}</Mono>{" "}
            <CopyButton value={snapshot.snapshot_hash} label="Copy hash" />
          </p>
          <DigestTable digest={snapshot.digest} />
          <p className="stage-a-dim">
            Write your prediction against this digest outside Cipher (paper or
            Notes). Nothing you write is entered here.
          </p>
        </div>
      )}
    </div>
  );
}

// ── (c)+(d) attestation and the one-time execution ──────────────────────────

function ExecuteCard({ snapshot, attested, setAttested, onExecuted, interlock }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The rehearsal interlock (server-enforced; rendered here read-only).
  // `interlock` is the status route's `execute_interlock` block: execution
  // requires a PASSED rehearsal for the CURRENT deployed commit, and the
  // server refuses with 409 otherwise — this only surfaces that state.
  const interlockBlocked = Boolean(interlock) && !interlock.passed_for_commit;

  const armed =
    Boolean(snapshot?.snapshot_id) && attested && !busy && !interlockBlocked;

  const execute = async () => {
    if (
      !window.confirm(
        "One-time execution of the reasoning cycle over snapshot " +
          `${snapshot.snapshot_id}. Reminder: your private prediction stays ` +
          "outside Cipher. Proceed?"
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const res = await post(`${BASE}/execute`, {
      snapshot_id: snapshot.snapshot_id,
      private_prediction_confirmed: true,
    });
    setBusy(false);
    if (res.status === "ok") {
      onExecuted(res.data);
    } else {
      setError(res);
    }
  };

  return (
    <div>
      {interlock && (
        <p className="stage-a-dim">
          Rehearsal interlock:{" "}
          {interlock.passed_for_commit ? (
            <>
              <span className="stage-a-status stage-a-passed">passed</span>{" "}
              for deployed commit{" "}
              <Mono>{(interlock.deployed_commit || "").slice(0, 12)}</Mono>
              {interlock.rehearsal_id ? (
                <>
                  {" "}
                  (<Mono>{interlock.rehearsal_id}</Mono>)
                </>
              ) : null}
            </>
          ) : (
            <>
              <span className="stage-a-status stage-a-failed">blocked</span>{" "}
              — {interlock.reason}
            </>
          )}
        </p>
      )}
      <label className="stage-a-attest">
        <input
          type="checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
        />{" "}
        My private prediction is written down outside Cipher.
      </label>
      <div>
        <button
          type="button"
          className="stage-a-btn stage-a-btn-primary"
          disabled={!armed}
          onClick={execute}
        >
          {busy ? "Executing…" : "Execute reasoning cycle"}
        </button>
        {!snapshot?.snapshot_id && (
          <span className="stage-a-dim"> Export a snapshot first.</span>
        )}
        {snapshot?.snapshot_id && !attested && (
          <span className="stage-a-dim">
            {" "}
            Confirm the prediction attestation to arm execution.
          </span>
        )}
      </div>
      <ErrorLine error={error} />
    </div>
  );
}

// ── grant reconciliation (OPTION2-EXTENSIONS-AND-COMPLIANCE-PARITY-RULED
//    item 3) — the Founder-UI surface for the existing founder route
//    POST /api/organization/agents/{id}/reconcile-grants, with a READ-ONLY
//    planned-vs-held preview first (same computation server-side, so what is
//    shown here is exactly what Reconcile would issue). ────────────────────

function GrantReconciliationAgent({ agent, onReconciled }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // SELECTIVE reconciliation (Founder finding, 2026-08-25): each missing
  // tool is individually selected; only checked tools are issued, and the
  // backend refuses any name outside the missing set. Nothing is
  // pre-checked — every issuance is an explicit Founder choice.
  const [selected, setSelected] = useState({});

  const missing = agent.missing || [];
  const chosen = missing.filter((m) => selected[m.tool]);
  const toggle = (tool) =>
    setSelected((prev) => ({ ...prev, [tool]: !prev[tool] }));
  const reconcile = async () => {
    if (
      !window.confirm(
        `Issue ${chosen.length} selected grant(s) to ` +
          `${agent.title || agent.agent_id} (${chosen
            .map((m) => m.tool)
            .join(", ")})? Unselected missing tools stay withheld.`
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const res = await post(
      `/organization/agents/${agent.agent_id}/reconcile-grants`,
      {
        reason:
          "Stage A panel — founder grant reconciliation after reviewing " +
          "the planned-vs-held preview (selective)",
        tools: chosen.map((m) => m.tool),
      }
    );
    setBusy(false);
    if (res.status === "ok") {
      setResult(res.data);
      onReconciled();
    } else {
      setError(res);
    }
  };

  return (
    <div className="stage-a-decision">
      <p>
        <strong>{agent.title || agent.agent_id}</strong>{" "}
        <Mono>{agent.agent_id}</Mono>
      </p>
      {agent.error ? (
        // A per-agent preview defect (e.g. a card Tool the registry lacks),
        // verbatim — the same defect would refuse this agent's reconcile.
        <p className="stage-a-error" role="alert">
          {agent.error}
        </p>
      ) : (
        <>
          <p className="stage-a-steps">
            planned: {(agent.planned || []).join(", ") || "—"} · already held:{" "}
            {agent.already_held}
          </p>
          {missing.map((m) => (
            <div key={m.tool} className="stage-a-steps">
              <input
                id={`grant-sel-${agent.agent_id}-${m.tool}`}
                type="checkbox"
                checked={!!selected[m.tool]}
                onChange={() => toggle(m.tool)}
              />{" "}
              <label htmlFor={`grant-sel-${agent.agent_id}-${m.tool}`}>
                {m.tool} ({m.capability_source_key || "?"}:{" "}
                {(m.operations || []).join("+")})
              </label>
            </div>
          ))}
          {missing.length > 0 ? (
            <button
              type="button"
              className="stage-a-btn"
              disabled={busy || chosen.length === 0}
              onClick={reconcile}
            >
              {busy
                ? "Reconciling…"
                : `Reconcile ${chosen.length} selected grant(s)`}
            </button>
          ) : (
            <p className="stage-a-dim">
              Nothing to issue — held grants cover the card.
            </p>
          )}
        </>
      )}
      <ErrorLine error={error} />
      {result && (
        <pre className="stage-a-pre">{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}

function GrantReconciliationCard() {
  const preview = useResource(
    "/organization/agents/all/grant-reconciliation-preview"
  );

  if (preview.status === "loading" || preview.status === "idle") {
    return <p className="stage-a-dim">Loading the grant preview…</p>;
  }
  if (preview.status === "refused") {
    return (
      <p className="stage-a-error" role="alert">
        Founder access required — the server refused the grant preview.
      </p>
    );
  }
  if (preview.status !== "ready") {
    return (
      <p className="stage-a-dim">
        The grant-reconciliation surface is not reachable (
        {preview.error || preview.status}). With CIPHER_ORGANIZATION_V1 off it
        does not exist.
      </p>
    );
  }

  const agents = (preview.data && preview.data.agents) || [];
  if (agents.length === 0) {
    return (
      <p className="stage-a-dim">
        No LIVE agents — grant reconciliation applies only to live agents.
      </p>
    );
  }
  return (
    <div>
      {agents.map((a) => (
        <GrantReconciliationAgent
          key={a.agent_id}
          agent={a}
          onReconciled={preview.reload}
        />
      ))}
    </div>
  );
}

// ── Brain (Wave 4A) — the founder-only Brain operator surface ──────────────
//    (WAVE4A-DIRECTED rulings 3/8/9.) Three server routes, mirrored here
//    with the panel's own patterns (useResource, post helper, confirm
//    dialog, verbatim errors, flag-off wording):
//      - GET  /organization/brain/snapshot-preview — read-only, ZERO writes
//        server-side: per-department snapshot hash + latest governed
//        judgment + coverage, before anything is frozen;
//      - POST /organization/brain/cycle — the Founder's conversational
//        trigger. While the Brain is DORMANT the server refuses with 409
//        carrying binding's own refusal, rendered VERBATIM below — this
//        panel exposes no Brain activation control at all;
//      - GET  /organization/brain/decisions — review-only decisions +
//        proposal dispositions (the proposal waits at its founder gate).

const BRAIN_BASE = "/organization/brain";

function BrainSectionsTable({ sections }) {
  if (!sections || sections.length === 0) return null;
  return (
    <table className="stage-a-table">
      <thead>
        <tr>
          <th>Department</th>
          <th>Snapshot hash</th>
          <th>Latest governed judgment</th>
          <th>Coverage</th>
        </tr>
      </thead>
      <tbody>
        {sections.map((s) => {
          const j = s.latest_judgment || {};
          const counts = s.headline_counts || {};
          return (
            <tr key={s.department}>
              <td>{s.department}</td>
              <td>
                <Mono>{(s.snapshot_hash || "").slice(0, 19)}</Mono>
              </td>
              <td className="stage-a-steps">
                {j.available ? (
                  <>
                    {j.decision_type} — confidence {j.confidence ?? "—"} —{" "}
                    <Mono>{j.decision_id}</Mono>
                    {j.proposal
                      ? ` · proposal ${j.proposal.task_id} (${j.proposal.state})`
                      : ""}
                  </>
                ) : (
                  <span className="stage-a-dim">
                    {j.reason || "no governed judgment yet"}
                  </span>
                )}
              </td>
              <td className="stage-a-steps">
                {Object.entries(counts)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ") || "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function BrainDecisionsList({ decisions }) {
  const data = decisions.data || {};
  if (decisions.status === "loading" || decisions.status === "idle") {
    return <p className="stage-a-dim">Loading Brain decisions…</p>;
  }
  if (decisions.status !== "ready") {
    return (
      <p className="stage-a-dim">
        Brain decisions are not readable ({decisions.error || decisions.status}
        ).
      </p>
    );
  }
  if (!data.decisions || data.decisions.length === 0) {
    return (
      <p className="stage-a-dim">
        No Brain reasoning decision has been recorded yet.
      </p>
    );
  }
  return (
    <div>
      {data.decisions.map((d) => (
        <div key={d.decision_id} className="stage-a-decision">
          <p>
            <strong>{d.decision_type}</strong> — confidence{" "}
            {d.confidence ?? "—"} — <Mono>{d.decision_id}</Mono> — doctrine{" "}
            <Mono>{d.subject_type_doctrine_version || "—"}</Mono>
          </p>
          {d.conclusion && <p>{d.conclusion}</p>}
        </div>
      ))}
      {(data.proposed_tasks || []).map((t) => (
        <div key={t.task_id} className="stage-a-proposal">
          <p>
            Proposed task <Mono>{t.task_id}</Mono> — state{" "}
            <Mono>{t.state}</Mono> — impact class <Mono>{t.impact_class}</Mono>
          </p>
          {t.statement && <p>{t.statement}</p>}
        </div>
      ))}
      <p className="stage-a-dim">
        Review-only: the Brain executes nothing — a proposed task waits at its
        founder approval gate.
      </p>
    </div>
  );
}

function BrainCard() {
  const preview = useResource(`${BRAIN_BASE}/snapshot-preview`);
  const decisions = useResource(`${BRAIN_BASE}/decisions`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const trigger = async () => {
    if (
      !window.confirm(
        "Freeze one composite snapshot and run the Brain reasoning cycle? " +
          "While the Brain is dormant the server will refuse (the canonical " +
          "dormancy refusal); this panel has no activation control."
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const res = await post(`${BRAIN_BASE}/cycle`, {});
    setBusy(false);
    if (res.status === "ok") {
      setSummary(res.data);
      preview.reload();
      decisions.reload();
    } else {
      setError(res);
    }
  };

  if (preview.status === "loading" || preview.status === "idle") {
    return <p className="stage-a-dim">Loading the Brain snapshot preview…</p>;
  }
  if (preview.status === "refused") {
    return (
      <p className="stage-a-error" role="alert">
        Founder access required — the server refused the Brain surface.
      </p>
    );
  }
  if (preview.status !== "ready") {
    return (
      <p className="stage-a-dim">
        The Brain surface is not reachable ({preview.error || preview.status}
        ). With CIPHER_ORGANIZATION_V1 off it does not exist.
      </p>
    );
  }

  const data = preview.data || {};
  return (
    <div>
      <p>
        Composite <Mono>{(data.snapshot_hash || "").slice(0, 19)}</Mono> —{" "}
        {data.size_bytes} bytes of {data.max_snapshot_bytes} — preview only,
        nothing frozen.
      </p>
      <BrainSectionsTable sections={data.sections} />
      <div>
        <button
          type="button"
          className="stage-a-btn stage-a-btn-primary"
          disabled={busy}
          onClick={trigger}
        >
          {busy ? "Running…" : "Run Brain cycle"}
        </button>
        <span className="stage-a-dim">
          {" "}
          Freezes a fresh composite and runs one reasoning cycle. Refused with
          the canonical dormancy error while the Brain is dormant.
        </span>
      </div>
      <ErrorLine error={error} />
      {summary && (
        <pre className="stage-a-pre">{JSON.stringify(summary, null, 2)}</pre>
      )}
      <BrainDecisionsList decisions={decisions} />
    </div>
  );
}

// ── Chief of Staff (Wave 4A item 4) — the founder-only CoS surface ─────────
//    (WAVE4A-COS-SURFACE-APPROVED.) Three server routes, mirrored with the
//    Brain card's own patterns:
//      - GET  /organization/cos/snapshot-preview — read-only, ZERO writes
//        server-side: per-department governed judgments/outcomes/
//        dispositions/verdicts, the surfaced contradictions, and the
//        embedded fail-closed readiness verdict (threshold fixed at 3);
//      - POST /organization/cos/cycle — the Founder's conversational
//        trigger. Refused 409 while NOT READY (shortfalls named) and while
//        DORMANT (the canonical refusal, rendered VERBATIM below — this
//        panel exposes no CoS activation control at all);
//      - GET  /organization/cos/decisions — review-only decisions +
//        proposal dispositions (the proposal waits at its founder gate).

const COS_BASE = "/organization/cos";

function CosSectionsTable({ sections }) {
  if (!sections || sections.length === 0) return null;
  return (
    <table className="stage-a-table">
      <thead>
        <tr>
          <th>Department</th>
          <th>Governed judgments</th>
          <th>Gradings / dispositions / verdicts</th>
          <th>Counts toward readiness</th>
        </tr>
      </thead>
      <tbody>
        {sections.map((s) => {
          const cov = (c) => (c ? `${c.included}/${c.total}` : "—");
          return (
            <tr key={s.department}>
              <td>{s.department}</td>
              <td className="stage-a-steps">
                {s.judgments_available ? (
                  (s.judgments || []).map((j) => (
                    <div key={j.decision_id}>
                      {j.decision_type} — confidence {j.confidence ?? "—"} —{" "}
                      <Mono>{j.decision_id}</Mono>
                      {j.freshness === "stale" ? " · STALE" : ""}
                    </div>
                  ))
                ) : (
                  <span className="stage-a-dim">
                    {s.judgments || "no governed judgment yet"}
                  </span>
                )}
              </td>
              <td className="stage-a-steps">
                outcomes {cov(s.outcomes_coverage)} · proposals{" "}
                {cov(s.proposal_dispositions_coverage)} · reviews{" "}
                {cov(s.no_action_reviews_coverage)}
              </td>
              <td>{s.counts_toward_readiness ? "yes" : "no"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CosDecisionsList({ decisions }) {
  const data = decisions.data || {};
  if (decisions.status === "loading" || decisions.status === "idle") {
    return <p className="stage-a-dim">Loading Chief of Staff decisions…</p>;
  }
  if (decisions.status !== "ready") {
    return (
      <p className="stage-a-dim">
        Chief of Staff decisions are not readable (
        {decisions.error || decisions.status}).
      </p>
    );
  }
  if (!data.decisions || data.decisions.length === 0) {
    return (
      <p className="stage-a-dim">
        No Chief of Staff reasoning decision has been recorded yet.
      </p>
    );
  }
  return (
    <div>
      {data.decisions.map((d) => (
        <div key={d.decision_id} className="stage-a-decision">
          <p>
            <strong>{d.decision_type}</strong> — confidence{" "}
            {d.confidence ?? "—"} — <Mono>{d.decision_id}</Mono> — doctrine{" "}
            <Mono>{d.subject_type_doctrine_version || "—"}</Mono>
          </p>
          {d.conclusion && <p>{d.conclusion}</p>}
        </div>
      ))}
      {(data.proposed_tasks || []).map((t) => (
        <div key={t.task_id} className="stage-a-proposal">
          <p>
            Proposed task <Mono>{t.task_id}</Mono> — state{" "}
            <Mono>{t.state}</Mono> — impact class <Mono>{t.impact_class}</Mono>
          </p>
          {t.statement && <p>{t.statement}</p>}
        </div>
      ))}
      <p className="stage-a-dim">
        Review-only: the Chief of Staff executes nothing — a proposed task
        waits at its founder approval gate.
      </p>
    </div>
  );
}

function CosReadinessLine({ readiness }) {
  if (!readiness) return null;
  if (readiness.ready) {
    return (
      <p>
        Readiness: <strong>ready</strong> —{" "}
        {readiness.counts?.heads_with_graded_outcomes} of{" "}
        {readiness.min_heads} required departments hold a graded production
        outcome.
      </p>
    );
  }
  return (
    <p className="stage-a-dim">
      Readiness: <strong>not ready</strong> (fail-closed; threshold fixed at{" "}
      {readiness.min_heads}) —{" "}
      {readiness.counts?.heads_with_graded_outcomes ?? 0} department(s) with
      graded outcomes. The cycle trigger below will be refused until the
      condition holds.
    </p>
  );
}

function CosCard() {
  const preview = useResource(`${COS_BASE}/snapshot-preview`);
  const decisions = useResource(`${COS_BASE}/decisions`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const trigger = async () => {
    if (
      !window.confirm(
        "Freeze one judgment-review snapshot and run the Chief of Staff " +
          "reasoning cycle? The server refuses while the readiness " +
          "condition is unmet (fail-closed) and while the CoS is dormant " +
          "(the canonical refusal); this panel has no activation control."
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const res = await post(`${COS_BASE}/cycle`, {});
    setBusy(false);
    if (res.status === "ok") {
      setSummary(res.data);
      preview.reload();
      decisions.reload();
    } else {
      setError(res);
    }
  };

  if (preview.status === "loading" || preview.status === "idle") {
    return (
      <p className="stage-a-dim">
        Loading the Chief of Staff snapshot preview…
      </p>
    );
  }
  if (preview.status === "refused") {
    return (
      <p className="stage-a-error" role="alert">
        Founder access required — the server refused the Chief of Staff
        surface.
      </p>
    );
  }
  if (preview.status !== "ready") {
    return (
      <p className="stage-a-dim">
        The Chief of Staff surface is not reachable (
        {preview.error || preview.status}). With CIPHER_ORGANIZATION_V1 off
        it does not exist.
      </p>
    );
  }

  const data = preview.data || {};
  const contradictions = (data.surfaced_contradictions || {}).rows || [];
  return (
    <div>
      <p>
        Judgment review <Mono>{(data.snapshot_hash || "").slice(0, 19)}</Mono>{" "}
        — {data.size_bytes} bytes of {data.max_snapshot_bytes} —
        judgment-review preview; nothing is frozen.
      </p>
      <CosReadinessLine readiness={data.readiness} />
      <CosSectionsTable sections={data.sections} />
      {contradictions.length > 0 && (
        <div className="stage-a-decision">
          <p>
            <strong>Surfaced contradictions</strong> (deterministic, governed
            records only):
          </p>
          {contradictions.map((c, i) => (
            <p key={i} className="stage-a-steps">
              {c.kind} — {c.department} — decision{" "}
              <Mono>{c.decision_id}</Mono>
              {c.review_id ? (
                <>
                  {" "}
                  · review <Mono>{c.review_id}</Mono>
                </>
              ) : null}
              {c.task_id ? (
                <>
                  {" "}
                  · task <Mono>{c.task_id}</Mono> ({c.task_state})
                </>
              ) : null}
            </p>
          ))}
        </div>
      )}
      <div>
        <button
          type="button"
          className="stage-a-btn stage-a-btn-primary"
          disabled={busy}
          onClick={trigger}
        >
          {busy ? "Running…" : "Run Chief of Staff cycle"}
        </button>
        <span className="stage-a-dim">
          {" "}
          Freezes a fresh judgment-review snapshot and runs one reasoning
          cycle. Refused fail-closed while not ready, and with the canonical
          dormancy error while the CoS is dormant.
        </span>
      </div>
      <ErrorLine error={error} />
      {summary && (
        <pre className="stage-a-pre">{JSON.stringify(summary, null, 2)}</pre>
      )}
      <CosDecisionsList decisions={decisions} />
    </div>
  );
}

// ── (e) the recorded result, review-only ────────────────────────────────────

function ResultCard({ runId }) {
  const result = useResource(runId ? `${BASE}/result/${runId}` : null, {
    skip: !runId,
  });

  if (!runId) {
    return (
      <p className="stage-a-dim">
        No run yet in this session. After execution, the recorded decision
        appears here for your judgment.
      </p>
    );
  }
  if (result.status === "loading" || result.status === "idle") {
    return <p className="stage-a-dim">Loading result…</p>;
  }
  if (result.status !== "ready") {
    return (
      <p className="stage-a-error">
        Could not read the result ({result.error || result.status}).
      </p>
    );
  }
  const data = result.data || {};
  return (
    <div>
      <p>
        Run <Mono>{data.run_id}</Mono> — status{" "}
        <Mono>{data.run_status || "recorded"}</Mono>
      </p>
      {(data.decisions || []).map((d) => {
        const p = d.payload || {};
        return (
          <div key={d.decision_id} className="stage-a-decision">
            <p>
              <strong>{p.decision_type}</strong> — confidence{" "}
              {p.confidence ?? "—"} — <Mono>{d.decision_id}</Mono>
            </p>
            {p.conclusion && <p>{p.conclusion}</p>}
            {p.reasoning && <pre className="stage-a-pre">{p.reasoning}</pre>}
            <p className="stage-a-dim">
              Evidence:{" "}
              {(p.evidence_refs || [])
                .map((e) =>
                  typeof e === "string"
                    ? e
                    : e.evidence_record_id || JSON.stringify(e)
                )
                .join(", ") || "none cited"}
            </p>
          </div>
        );
      })}
      {(data.proposed_tasks || []).map((t) => (
        <div key={t.task_id} className="stage-a-proposal">
          <p>
            Proposed task <Mono>{t.task_id}</Mono> — state{" "}
            <Mono>{t.state}</Mono> — impact class <Mono>{t.impact_class}</Mono>
          </p>
          {t.statement && <p>{t.statement}</p>}
        </div>
      ))}
      <p className="stage-a-dim">
        Review-only during Stage A: this panel deliberately exposes no control
        that approves, dispatches or executes the proposal. The task waits at
        its founder gate.
      </p>
    </div>
  );
}

// ── the panel ───────────────────────────────────────────────────────────────

export default function StageAPilot() {
  const status = useResource(`${BASE}/status`);
  const [snapshot, setSnapshot] = useState(null);
  const [attested, setAttested] = useState(false);
  const [summary, setSummary] = useState(null);

  const refresh = useCallback(() => status.reload(), [status]);

  if (status.status === "refused") {
    return (
      <div className="stage-a">
        <PageHead
          title="Stage A Pilot"
          subtitle="Founder access required — the server refused this surface."
        />
      </div>
    );
  }
  if (status.status === "failed" || status.status === "offline") {
    return (
      <div className="stage-a">
        <PageHead
          title="Stage A Pilot"
          subtitle={`The Stage A surface is not reachable (${
            status.error || status.status
          }). With CIPHER_ORGANIZATION_V1 off it does not exist.`}
        />
      </div>
    );
  }

  const data = status.data || {};
  const runId =
    summary?.run_id ||
    (data.snapshots || []).find((s) => s.executed)?.linkage?.run_id ||
    null;

  return (
    <div className="stage-a">
      <PageHead
        title="Stage A Pilot"
        subtitle="Head of Testing reasoning pilot — the blind protocol, through Cipher. Each step is an explicit click; the server enforces the order."
      />

      <Card
        title="Post-deploy rehearsal"
        note="Runs automatically after each deploy, in a segregated rehearsal database. Read-only evidence."
      >
        <RehearsalCard rehearsals={data.rehearsals} />
      </Card>

      <Card
        title="Materialize auto-run"
        note="Automatic after each deploy, through the invariant gate. Read-only evidence."
      >
        <MaterializeCard runs={data.materialize_runs} />
        <MaterializeApproval />
      </Card>

      <Card
        title="1 — Formal activation of the Head of Testing"
        note="Your approval, through the existing founder activation route."
      >
        <ActivationCard
          head={data.head}
          headAgentId={data.head_agent_id}
          onChanged={refresh}
        />
      </Card>

      <Card
        title="1c — Dormant specialists"
        note="Every dormant specialist (50), through the same founder route and the same confirm. All carry readiness `unvalidated`, so each activation is recorded as activated_despite_readiness. Two WILL refuse on an uncertified card Tool — Competitive Creative Research and Offer Qualification & Handoff — and that refusal is correct, not a bug."
      >
        <DormantSpecialists />
      </Card>

      <Card
        title="1b — Formal activation of the Creative Brief & Concept Specialist"
        note="Same founder activation route, same grants-at-activation path. Its readiness is shown because activation does not gate on it."
      >
        <SpecialistActivation />
      </Card>

      <Card
        title="Grant reconciliation"
        note="Per LIVE agent: the card-approved grants vs what is held, read-only, before anything is issued. Reconcile issues exactly the missing set shown — the same server-side computation produces both."
      >
        <GrantReconciliationCard />
      </Card>

      <Card
        title="Brain (Wave 4A)"
        note="Founder-triggered only — no scheduler exists for this. Preview shows every department's own snapshot hash and latest governed judgment (identity never blended away); the trigger freezes a composite and runs one reasoning cycle; the dormancy refusal renders verbatim."
      >
        <BrainCard />
      </Card>

      <Card
        title="Chief of Staff (Wave 4A)"
        note="Founder-triggered only — no scheduler exists for this. Preview shows the governed judgment stream (Head judgments, gradings, dispositions, review verdicts), the surfaced contradictions, and the fail-closed readiness verdict (threshold fixed at 3); the trigger freezes a judgment-review snapshot and runs one reasoning cycle; the not-ready and dormancy refusals render verbatim."
      >
        <CosCard />
      </Card>

      <Card
        title="2 — Export the frozen snapshot"
        note="The evidence the Head will reason over, frozen and hashed before any model sees it."
      >
        <SnapshotCard
          snapshot={snapshot}
          setSnapshot={setSnapshot}
          onChanged={refresh}
        />
      </Card>

      <Card
        title="3 — Confirm the prediction, then execute (one time)"
        note="Cipher stores no prediction. The server refuses execution without the attestation, and refuses a second execution of the same snapshot."
      >
        <ExecuteCard
          snapshot={snapshot}
          attested={attested}
          setAttested={setAttested}
          interlock={data.execute_interlock}
          onExecuted={(s) => {
            setSummary(s);
            refresh();
          }}
        />
      </Card>

      <Card
        title="4 — The recorded decision"
        note="What you judge against your private prediction. Review-only."
      >
        <ResultCard runId={runId} />
      </Card>
    </div>
  );
}
